import { useInjection } from 'inversify-react';
import { useCallback, useMemo, useRef } from 'react';

import { useProjects } from '@desygner/ui-features-projects';

import { initial as zoomConfig } from '../../../config/zoom';
import { TYPES } from '../../../inversify.types';
import { IViewerStoreInjector } from '../../../store';
import { isMobileDevice } from '../../../utils/device.util';
import { calculateZoomForFitToView } from '../../../utils/zoom.util';
import { useResponsive } from '../../hooks/useResponsive';
import { MultiPageMode } from './MultiPageMode/MultiPageMode';
import { SinglePageMode } from './SinglePageMode/SinglePageMode';
import { VirtualList } from './VirtualList/VirtualList';

const GAP_BETWEEN_PAGES_IN_PX = 24;
const LOOK_AHEAD_PAGES = 3;

const FALLBACK_PAGE_WIDTH = window.innerWidth;

export const Viewer = () => {
  const useViewerStore = useInjection<IViewerStoreInjector>(TYPES.Store.Viewer);
  const isSinglePageMode = useViewerStore((state) => state.pageMode === 'single');
  const { project } = useProjects();

  const pages = project?.pages;

  const zoomLevel = useViewerStore((state) => state.zoom / 100);
  const setZoom = useViewerStore((state) => state.setZoom);
  const pageDimension = useViewerStore((state) => state.pageDimension);

  const pageDimentionWidth = pageDimension?.width || FALLBACK_PAGE_WIDTH;
  const isFitToView = useViewerStore((state) => state.viewOptions === 'fit');
  const setSelectedPageNumber = useViewerStore((state) => state.setSelectedPageNumber);
  const setViewOptions = useViewerStore((state) => state.setViewOptions);
  const { isTabletOrMobile } = useResponsive();

  const ref = useRef<HTMLDivElement>(null);

  const estimateSize = useCallback(
    (index: number) => {
      const { height, width } = pages?.[index] || {};

      if (!height || !width) {
        return FALLBACK_PAGE_WIDTH * zoomLevel;
      }

      const originalSize = (height / width) * pageDimentionWidth;

      return originalSize * zoomLevel;
    },
    [pageDimentionWidth, pages?.length, zoomLevel]
  );

  const paddingY = useMemo(() => {
    if (isSinglePageMode) {
      return 0;
    }

    const paddingY = isTabletOrMobile ? zoomConfig.padding.y.mobile : zoomConfig.padding.y.desktop;

    return paddingY;
  }, [isSinglePageMode, isTabletOrMobile]);

  const onWindowResize = useCallback(() => {
    if (!isFitToView || isMobileDevice()) return;

    const fitToViewPercentage = calculateZoomForFitToView(pageDimension);

    setZoom(fitToViewPercentage);
    setViewOptions('fit');
  }, [isFitToView, pageDimension, setViewOptions, setZoom]);

  const updateActiveItemOnScroll = useCallback(
    (activePageNumber: number) => {
      setSelectedPageNumber(activePageNumber);
    },
    [setSelectedPageNumber]
  );

  if (!pages) return null;

  if (isSinglePageMode) {
    return pages.map((page, index) => <SinglePageMode key={page.id} page={page} index={index} />);
  }

  return (
    <VirtualList
      items={pages}
      renderItem={(item, virtualItem, virtualizer) => (
        <MultiPageMode
          key={item.id}
          item={item}
          virtualItem={virtualItem}
          virtualizer={virtualizer}
        />
      )}
      zoomLevel={zoomLevel}
      pageWidth={pageDimentionWidth}
      virtualizerOptions={{
        estimateSize,
        overscan: LOOK_AHEAD_PAGES,
        scrollMargin: ref.current?.offsetTop ?? 0,
        paddingStart: paddingY,
        paddingEnd: paddingY,
        gap: GAP_BETWEEN_PAGES_IN_PX * zoomLevel,
        count: pages.length,
        getItemKey(index) {
          return pages[index].id;
        },
      }}
      updateActiveItemOnScroll={updateActiveItemOnScroll}
      onWindowResize={onWindowResize}
    />
  );
};
